import { createTheme } from '@mui/material/styles'
import { breakpoints } from '../Breakpoints'
import { fonts, pixelToRemMap, typography as zTypography } from '../Typography'
import { space } from '../Core'
import { dFlexCenter } from '../UtilityStyles'
import { createMuiButtonOverrides } from '../overrides/MuiButtonOverrides'
import { createMuiCheckboxOverrides } from '../overrides/MuiCheckboxOverrides'
import { generateColours, applyAlpha } from './utils'

export const createMuiColors = (selectedPalette: any) => ({
    primary: {
        white: selectedPalette.primary[100],
        blueMain: selectedPalette.primary[40],
        brightBlue: selectedPalette.primary[50],
        lightBlue: selectedPalette.primary[95],
        blackAlt: selectedPalette.neutral[40],
        blackAltOpacity80: applyAlpha(selectedPalette.neutral[40], 0.8),
        blackAltOpacity60: applyAlpha(selectedPalette.neutral[40], 0.6),
        blackAltOpacity23: applyAlpha(selectedPalette.neutral[40], 0.23),
        errorRedMain: selectedPalette.error[40],
        lightGrey: 'rgba(209, 209, 209, 0.09)', // no tonal match
        lightestGrey: 'rgba(0, 0, 0, 0.04)', // no tonal match
        grey: selectedPalette.neutral[60],
        darkGrey: 'rgba(0, 0, 0, 0.09)', // no tonal match
        disabledGrey: 'rgba(0, 0, 0, 0.38)', // no tonal match
        green: 'rgb(88, 196, 198)', // no tonal match.. is this even used?
        successGreen: selectedPalette.success[70],
        darkGreen: selectedPalette.success[40],
        yellow: selectedPalette.secondary[90]
    },
    secondary: {
        lightGrey: selectedPalette.neutralWarm[85]
    },
    prominent: generateColours(
        ['blue', 31, 46, 207],
        ['darkBlue', 13, 20, 89],
        ['lightGrey', 224, 224, 224],
        ['darkGrey', 51, 51, 51]
    ),
    prominentColors: {
        lightBlue: selectedPalette.primary[90]
    }
})

export const muiTypography = {
    body: {
        label: {
            fontSize: '16px',
            fontWeight: 'normal',
            lineHeight: '24px',
            fontFamily: fonts.regular,
            letterSpacing: '0.4px',
            small: {
                fontSize: '12px',
                lineHeight: '14px',
                fontFamily: fonts.medium
            }
        },
        checkbox: {
            label: {
                normal: {
                    fontSize: '16px',
                    lineHeight: '24px',
                    fontFamily: fonts.regular,
                    letterSpacing: '0px'
                }
            }
        },
        text: {
            normal: {
                fontSize: '14px',
                lineHeight: '20px',
                fontFamily: fonts.regular
            },
            medium: {
                fontSize: '16px',
                lineHeight: '14px',
                fontFamily: fonts.medium
            },
            small: {
                fontSize: '9px',
                lineHeight: '14px',
                fontFamily: fonts.medium
            }
        }
    }
}

/* TODO: These should be in the theme only but short term solution until Npm package is working */
// Update MUI Theme breakpoints
declare module '@mui/material/styles' {
    interface BreakpointOverrides {
        xs: true
        sm: true
        md: true
        mdplus: true
        lg: true
        xsDesktop: true
        desktop: true
        wide: true
        xl: true
        ultraWide: true
    }
}

// Update the Typography's variant prop options in line with M3's design system
declare module '@mui/material/styles' {
    interface TypographyVariants {
        rowHeading: React.CSSProperties
        displayLarge: React.CSSProperties
        displayMedium: React.CSSProperties
        displaySmall: React.CSSProperties
        headlineLarge: React.CSSProperties
        headlineMedium: React.CSSProperties
        headlineSmall: React.CSSProperties
        titleLarge: React.CSSProperties
        titleMedium: React.CSSProperties
        titleSmall: React.CSSProperties
        labelXLarge: React.CSSProperties
        labelLarge: React.CSSProperties
        labelMedium: React.CSSProperties
        labelSmall: React.CSSProperties
        bodyLarge: React.CSSProperties
        bodyMedium: React.CSSProperties
        bodySmall: React.CSSProperties
    }
    interface TypographyVariantsOptions {
        rowHeading?: React.CSSProperties
        displayLarge: React.CSSProperties
        displayMedium: React.CSSProperties
        displaySmall: React.CSSProperties
        headlineLarge: React.CSSProperties
        headlineMedium: React.CSSProperties
        headlineSmall: React.CSSProperties
        titleLarge: React.CSSProperties
        titleMedium: React.CSSProperties
        titleSmall: React.CSSProperties
        labelXLarge: React.CSSProperties
        labelLarge: React.CSSProperties
        labelMedium: React.CSSProperties
        labelSmall: React.CSSProperties
        bodyLarge: React.CSSProperties
        bodyMedium: React.CSSProperties
        bodySmall: React.CSSProperties
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        rowHeading: true
        displayLarge: true
        displayMedium: true
        displaySmall: true
        headlineLarge: true
        headlineMedium: true
        headlineSmall: true
        titleLarge: true
        titleMedium: true
        titleSmall: true
        labelXLarge: true
        labelLarge: true
        labelMedium: true
        labelSmall: true
        bodyLarge: true
        bodyMedium: true
        bodySmall: true
    }
}


declare module '@mui/material/styles' {
    interface Theme {
        customComponents: {
            progressBar: {
                bar: React.CSSProperties
                progress: React.CSSProperties
                carWrapper: React.CSSProperties
                flagWrapper: React.CSSProperties
                finalFlagLeftWrapper: React.CSSProperties
                finalFlagRightWrapper: React.CSSProperties
            }
        }
    }

    // Optional: if you use `createTheme` with partials
    interface ThemeOptions {
        customComponents?: {
            progressBar?: {
                bar?: React.CSSProperties
                progress?: React.CSSProperties
                carWrapper?: React.CSSProperties
                flagWrapper?: React.CSSProperties
                finalFlagLeftWrapper?: React.CSSProperties
                finalFlagRightWrapper?: React.CSSProperties
            }
        }
    }
}

export const createAppTheme = (selectedPalette: any) => {
    const muiColors = createMuiColors(selectedPalette)

    return createTheme({
        ...breakpoints,
        spacing: [0, 4, 8, 10, 12, 16, 20, 24, 32, 40, 48, 56, 64, 96, 128],
        customPalette: selectedPalette,
        typography: {
            ...zTypography,
            h2: {
                fontSize: '34px',
                lineHeight: '42px',
                fontFamily: fonts.regular,
                color: selectedPalette.neutral[40]
            },
            h3: {
                fontSize: '18px',
                fontFamily: fonts.medium,
                color: selectedPalette.neutral[40],
                '@media (min-width: 768px)': {
                    fontSize: '20px'
                }
            },
            body1: {
                ...muiTypography.body.text.normal
            },
            body2: {
                fontSize: '14px',
                lineHeight: '20px',
                fontFamily: fonts.medium
            },
            rowHeading: {
                fontSize: '16px',
                lineHeight: '24px',
                fontFamily: fonts.medium
            }
        },
        components: {
            MuiTypography: {
                defaultProps: {
                    variantMapping: {
                        // Map the new variant to render a <p> by default
                        rowHeading: 'p'
                    }
                }
            },
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        '&.disabled': {
                            backgroundColor: 'unset',
                            '.MuiInputBase-root::before, .MuiInputBase-root:hover::before': {
                                borderBottom: `1px dashed ${muiColors.primary.disabledGrey}`
                            },
                            '&.MuiTextField-root': {
                                '&:hover': {
                                    '.MuiFormLabel-root': {
                                        color: muiColors.primary.disabledGrey
                                    }
                                },
                                '.MuiFormLabel-root': {
                                    color: muiColors.primary.disabledGrey
                                }
                            }
                        },
                        '&:not(.Mui-disabled):hover': {
                            '.MuiFormLabel-root': {
                                color: selectedPalette.neutral[40],
                                '&.Mui-error': {
                                    color: selectedPalette.error[40]
                                },
                                '@media (min-width: 1025px)': {
                                    color: selectedPalette.primary[50]
                                }
                            }
                        },
                        '& .MuiFormLabel-root': {
                            ...muiTypography.body.label,
                            pointerEvents: 'none',
                            '&.MuiInputLabel-root.Mui-error': {
                                color: selectedPalette.error[40]
                            },
                            '&:not(.Mui-disabled)': {
                                color: selectedPalette.neutral[40],
                                '&.MuiInputLabel-outlined': {
                                    color: muiColors.primary.blackAltOpacity80,
                                    '&.Mui-error': {
                                        color: selectedPalette.error[40]
                                    }
                                },
                                '&:not(.MuiInputLabel-shrink)': {
                                    color: muiColors.primary.blackAltOpacity60
                                }
                            },
                            '&:not(.Mui-disabled).Mui-focused': {
                                color: selectedPalette.primary[50],
                                '&.MuiInputLabel-outlined': {
                                    color: selectedPalette.primary[40]
                                }
                            }
                        },
                        '&.outlined': {
                            '& .MuiFormLabel-root': {
                                '&:not(.Mui-disabled)': {
                                    '&.MuiInputLabel-outlined': {
                                        '&.Mui-error': {
                                            color: selectedPalette.error[40]
                                        }
                                    }
                                }
                            },
                            '&.disabled': {
                                '& .MuiFormLabel-root': {
                                    color: muiColors.primary.blackAltOpacity23
                                },
                                input: {
                                    color: muiColors.primary.blackAltOpacity23
                                },
                                '& fieldset': {
                                    border: `1px dotted ${muiColors.primary.blackAltOpacity23}`
                                },
                                '&:hover': {
                                    '& .MuiFormLabel-root': {
                                        color: muiColors.primary.blackAltOpacity23
                                    },
                                    '& fieldset': {
                                        borderColor: muiColors.primary.blackAltOpacity23
                                    }
                                }
                            },
                            '&.MuiTextField-root': {
                                margin: '0 0 10px 0',
                                '.MuiFormHelperText-root': {
                                    fontSize: '12px',
                                    color: muiColors.primary.blackAltOpacity80,
                                    '&.Mui-error': {
                                        color: selectedPalette.error[40]
                                    }
                                }
                            },
                            '& .MuiInputLabel-shrink': {
                                paddingRight: '5px',
                                backgroundColor: selectedPalette.primary[100]
                            }
                        },
                        '& .MuiInputBase-root': {
                            fontFamily: muiTypography.body.label.fontFamily
                        },
                        '&.MuiTextField-root': {
                            margin: '0 0 10px 0'
                        },
                        '&.MuiSelect-wrapper': {
                            '& .MuiInputLabel-outlined': {
                                background: selectedPalette.primary[100],
                                '&.MuiInputLabel-shrink': {
                                    padding: '0 5px',
                                    marginLeft: '-3px'
                                }
                            }
                        },
                        '&.darker-input': {
                            '&:not(.disabled)': {
                                '& .MuiInputBase-root.MuiFilledInput-root': {
                                    backgroundColor: selectedPalette.neutral[90],
                                    '&:hover, &.Mui-focused': {
                                        backgroundColor: selectedPalette.primary[95]
                                    }
                                },
                                '&:hover': {
                                    '& .MuiFormLabel-root.MuiInputLabel-root': {
                                        color: selectedPalette.primary[50],
                                        '&.Mui-error': {
                                            color: selectedPalette.error[40]
                                        }
                                    }
                                }
                            },
                            '&.disabled': {
                                '& .MuiInputBase-root.MuiFilledInput-root': {
                                    backgroundColor: selectedPalette.primary[100]
                                }
                            }
                        }
                    }
                }
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        '& .MuiTypography-root': {
                            ...muiTypography.body.checkbox.label.normal,
                            color: selectedPalette.neutral[60]
                        },
                        '& .MuiSvgIcon-root': {
                            minWidth: '18px',
                            minHeight: '18px'
                        }
                    }
                }
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        ...muiTypography.body.label,
                        marginLeft: '12px',
                        '&.Mui-focused': {
                            backgroundColor: 'unset'
                        }
                    }
                }
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        // SHARED INPUT STYLES
                        '&:not(.Mui-disabled) .MuiTypography-root': {
                            color: selectedPalette.neutral[40]
                        },
                        'input[disabled]': {
                            backgroundColor: muiColors.primary.white
                        },
                        input: {
                            color: selectedPalette.neutral[40],
                            fontFamily: muiTypography.body.label.fontFamily
                        },
                        // OUTLINED INPUT SPECIFIC STYLES
                        '&.MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '& fieldset': {
                                borderColor: selectedPalette.neutral[40]
                            },
                            '&:hover': {
                                '& fieldset': {
                                    borderColor: selectedPalette.primary[60]
                                }
                            },
                            '&.Mui-focused': {
                                '& fieldset': {
                                    borderColor: selectedPalette.primary[40]
                                },
                                input: {
                                    color: selectedPalette.neutral[40]
                                }
                            },
                            '&.Mui-error': {
                                '& fieldset': {
                                    borderColor: selectedPalette.error[40]
                                }
                            },
                            input: {
                                color: selectedPalette.neutral[40],
                                fontSize: '16px'
                            },
                            '& .MuiInputAdornment-root.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
                                '@media (min-width: 768px)': {
                                    marginTop: '3px'
                                }
                            }
                        },
                        // FILLED INPUT SPECIFIC STYLES
                        '&.MuiFilledInput-root': {
                            backgroundColor: muiColors.primary.lightGrey,
                            '&:after': {
                                borderBottom: `2px solid ${selectedPalette.primary[50]}`
                            },
                            '&.Mui-disabled': {
                                backgroundColor: selectedPalette.primary[95]
                            },
                            '&.Mui-focused:hover': {
                                backgroundColor: selectedPalette.primary[95]
                            },
                            '&:hover': {
                                backgroundColor: muiColors.primary.lightGrey,
                                '@media (min-width: 1025px)': {
                                    backgroundColor: selectedPalette.primary[95]
                                }
                            },
                            '& .MuiInputAdornment-root.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
                                marginTop: '15px',
                                '@media (min-width: 768px)': {
                                    marginTop: '19px'
                                }
                            }
                        }
                    }
                }
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        margin: '10px 0',
                        width: '100%',
                        '&.w50': {
                            flexBasis: '50%'
                        },
                        '&.w50.spacing-right': {
                            flexBasis: 'calc(50% - 19px)',
                            '&.outlined': {
                                flexBasis: 'calc(50% - 10px)'
                            }
                        },
                        '&.small': {
                            flexBasis: '30%'
                        },
                        '&.large': {
                            flexBasis: '40%'
                        },
                        '&.small.spacing-right': {
                            flexBasis: 'calc(30% - 19px)',
                            '&.outlined': {
                                flexBasis: 'calc(30% - 10px)'
                            }
                        },
                        '&.spacing-right': {
                            marginRight: '19px',
                            '&.outlined': {
                                marginRight: '10px'
                            }
                        },
                        '&.multiple-inputs': {
                            '& .MuiFormHelperText-root': {
                                marginBottom: '-23px',
                                marginTop: '-1px',
                                marginLeft: '14px'
                            }
                        }
                    }
                }
            },
            MuiFilledInput: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused': {
                            backgroundColor: selectedPalette.primary[95]
                        },
                        '&.darker-input:not(.disabled)': {
                            backgroundColor: muiColors.primary.darkGrey,
                            '&:hover, &.Mui-focused': {
                                backgroundColor: muiColors.primary.lightBlue
                            },
                            '&:hover .MuiFormLabel-root': {
                                color: muiColors.primary.brightBlue,
                                '&.Mui-error': {
                                    color: muiColors.primary.errorRedMain
                                }
                            }
                        },
                        '&.darker-input.disabled': {
                            backgroundColor: muiColors.primary.white
                        }
                    },
                    input: {
                        '&:hover': {
                            borderRadius: '4px 4px 0 0'
                        }
                    }
                }
            },
            MuiSelect: {
                styleOverrides: {
                    select: {
                        color: selectedPalette.neutral[40],
                        '&.MuiSelect-filled': {
                            '&:focus': {
                                backgroundColor: selectedPalette.primary[95]
                            }
                        },
                        '& #mui-component-select-employment-status-list': {
                            color: muiColors.primary.blackAlt
                        }
                    }
                }
            },
            MuiList: {
                styleOverrides: {
                    root: {
                        '& .MuiButtonBase-root': {
                            fontFamily: muiTypography.body.label.fontFamily
                        }
                    }
                }
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        wordBreak: 'break-word',
                        whiteSpace: 'break-spaces'
                    }
                }
            },
            MuiAutocomplete: {
                styleOverrides: {
                    listbox: {
                        '& li': {
                            ...muiTypography.body.checkbox.label.normal,
                            '&.Mui-focused, &:hover': {
                                backgroundColor: `${muiColors.primary.lightestGrey}`
                            }
                        }
                    },
                    option: {
                        '&[aria-selected="true"]': {
                            backgroundColor: `${muiColors.primary.lightestGrey}`
                        }
                    }
                }
            },
            MuiStepLabel: {
                styleOverrides: {
                    label: {
                        ...muiTypography.body.text.normal,
                        color: selectedPalette.neutral[40],
                        '@media (max-width: 359px)': {
                            fontSize: '12px'
                        }
                    }
                }
            },
            MuiStepConnector: {
                styleOverrides: {
                    line: {
                        borderColor: muiColors.secondary.lightGrey
                    }
                }
            },
            MuiButton: {
                styleOverrides: createMuiButtonOverrides(selectedPalette)
            },
            MuiCheckbox: {
                styleOverrides: createMuiCheckboxOverrides(selectedPalette)
            },
            MuiCard: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        ...dFlexCenter,
                        flexDirection: 'column',
                        borderRadius: '12px',
                        border: `1px solid ${selectedPalette.neutral[80]}`,
                        boxShadow: 'none',
                        padding: `${space.spacing[11]}px ${space.spacing[7]}px`,
                        width: '100%',
                        marginBottom: `${space.spacing[4]}px`,
                        boxSizing: 'border-box',
                        [theme.breakpoints.up('lg')]: {
                            maxWidth: pixelToRemMap[360],
                            marginBottom: `${space.spacing[10]}px`
                        }
                    })
                }
            },
            MuiAccordion: {
                styleOverrides: {
                    root: () => ({
                        marginBottom: '1px',
                        '&.Mui-expanded': {
                            marginBottom: `${space.spacing[6]}px`,
                            borderBottom: `2px solid ${selectedPalette.neutral[60]}`,
                            '&:last-of-type': {
                                marginBottom: `${space.spacing[6]}px`
                            }
                        }
                    })
                }
            },
            MuiAccordionSummary: {
                styleOverrides: {
                    root: () => ({
                        margin: 0,
                        padding: `${space.spacing[6]}px`
                    }),
                    content: () => ({
                        margin: 0,
                        zIndex: '-1',
                        '&.Mui-expanded': {
                            margin: 0
                        }
                    })
                }
            },
            MuiAccordionDetails: {
                styleOverrides: {
                    root: () => ({
                        ...zTypography.bodyMedium,
                        padding: `0 ${space.spacing[6]}px ${space.spacing[6]}px`,
                        p: {
                            marginTop: 0,
                            ...zTypography.bodyMedium
                        }
                    })
                }
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: () => ({
                        textTransform: 'none',
                        minHeight: '50px',
                        margin: `0px !important`,
                        borderRadius: `${space.spacing[3]}px !important`,
                        border: `1px solid ${selectedPalette.neutral[80]} !important`,
                        textAlign: 'center',
                        justifyContent: 'center',
                        padding: `${space.spacing[0]}px  ${space.spacing[5]}px !important`,
                        fontWeight: 500,
                        fontFamily: fonts.medium,
                        fontSize: pixelToRemMap[16],
                        lineHeight: pixelToRemMap[26],
                        cursor: 'pointer',
                        color: selectedPalette.neutral[50],
                        '&.Mui-selected': {
                            backgroundColor: selectedPalette.tertiary[95],
                            color: selectedPalette.primary[40],
                            border: `2px solid ${selectedPalette.primary[40]} !important`
                        },
                        '@media (hover: hover)': { // Only apply these styles if the browser supports proper/true/real/non-emulated hovering (e.g. has a mouse-like primary input device)
                            '&:hover': {
                                backgroundColor: selectedPalette.tertiary[95],
                                border: `1px solid ${selectedPalette.tertiary[95]} !important`
                            },
                            '&.Mui-selected:hover': {
                                backgroundColor: selectedPalette.tertiary[95],
                                color: selectedPalette.primary[40],
                                border: `2px solid ${selectedPalette.primary[40]} !important`
                            }
                        }
                    })
                }
            }
        },
        customComponents: {
            progressBar: {
                bar: {
                    height: 6,
                    borderRadius: 40,
                    marginTop: 12,
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    backgroundColor: selectedPalette.primary[80]
                },
                progress: {
                    height: '100%',
                    borderRadius: 16,
                    transition: 'width 0.4s ease',
                    backgroundColor: selectedPalette.primary[40]
                },
                carWrapper: {
                    position: 'absolute',
                    top: -28,
                    left: 0
                },
                flagWrapper: {
                    position: 'absolute',
                    top: -30,
                    right: 0,
                    display: 'flex',
                    alignItems: 'flex-end'
                },
                finalFlagLeftWrapper: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                },
                finalFlagRightWrapper: {
                    marginLeft: 4
                }
            }
        }
    })
}